import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  SwipeableDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Avatar,
  Divider,
  Badge,
  Switch,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Add as AddIcon,
  Photo as PhotoIcon,
  CalendarMonth as CalendarIcon,
  Reviews as ReviewsIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@mui/material/styles';

/**
 * Mobile hamburger navigation drawer
 * Provides full navigation menu for mobile devices
 */
const MobileNav = ({ 
  open, 
  onOpen, 
  onClose, 
  darkMode, 
  onToggleDarkMode,
  notifications = {},
  user = { name: 'KashLegal User', avatar: '' }
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const navigationItems = [
    {
      text: 'Dashboard',
      icon: DashboardIcon,
      path: '/dashboard',
      badge: notifications.dashboard || 0,
      description: 'Overview and quick stats',
    },
    {
      text: 'Create Post',
      icon: AddIcon,
      path: '/posts/create',
      badge: 0,
      description: 'Create new GBP content',
      primary: true,
    },
    {
      text: 'Photo Manager',
      icon: PhotoIcon,
      path: '/photos',
      badge: notifications.photos || 0,
      description: 'Manage office photos',
    },
    {
      text: 'Calendar',
      icon: CalendarIcon,
      path: '/calendar',
      badge: notifications.calendar || 0,
      description: 'Scheduled posts',
    },
    {
      text: 'Reviews',
      icon: ReviewsIcon,
      path: '/reviews',
      badge: notifications.reviews || 0,
      description: 'Manage client reviews',
    },
    {
      text: 'Analytics',
      icon: AnalyticsIcon,
      path: '/analytics',
      badge: notifications.analytics || 0,
      description: 'Performance insights',
    },
    {
      text: 'Settings',
      icon: SettingsIcon,
      path: '/settings',
      badge: 0,
      description: 'App configuration',
    },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const isCurrentPath = (path) => {
    return location.pathname.startsWith(path);
  };

  const DrawerContent = () => (
    <Box
      sx={{
        width: 320,
        height: '100%',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight="bold" color="primary">
            KashLegal GBP
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* User Profile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              src={user.avatar}
              sx={{
                width: 48,
                height: 48,
                background: theme.palette.primary.gradient,
                border: `2px solid ${theme.palette.primary.main}`,
              }}
            >
              {user.name.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="600">
                {user.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Office Manager
              </Typography>
            </Box>
          </Box>
        </motion.div>
      </Box>

      {/* Navigation Items */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List sx={{ px: 1 }}>
          {navigationItems.map((item, index) => {
            const IconComponent = item.icon;
            const isActive = isCurrentPath(item.path);
            
            return (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + (index * 0.05) }}
              >
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      py: 1.5,
                      px: 2,
                      background: isActive 
                        ? theme.palette.primary.main 
                        : 'transparent',
                      color: isActive 
                        ? 'white' 
                        : theme.palette.text.primary,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: isActive 
                          ? theme.palette.primary.dark 
                          : 'rgba(30, 58, 138, 0.08)',
                        transform: 'translateX(4px)',
                      },
                      ...(item.primary && !isActive && {
                        background: 'rgba(30, 58, 138, 0.1)',
                        border: `1px solid ${theme.palette.primary.main}`,
                      }),
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Badge
                        badgeContent={item.badge}
                        color="error"
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: '0.625rem',
                            height: '16px',
                            minWidth: '16px',
                          },
                        }}
                      >
                        <IconComponent 
                          sx={{ 
                            color: isActive ? 'white' : 'inherit',
                            fontSize: item.primary ? '1.5rem' : '1.25rem',
                          }} 
                        />
                      </Badge>
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Typography 
                          variant="subtitle2" 
                          fontWeight={isActive ? 600 : 500}
                        >
                          {item.text}
                        </Typography>
                      }
                      secondary={
                        <Typography 
                          variant="caption" 
                          color={isActive ? 'rgba(255,255,255,0.8)' : 'text.secondary'}
                        >
                          {item.description}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </motion.div>
            );
          })}
        </List>
      </Box>

      <Divider />

      {/* Footer Controls */}
      <Box sx={{ p: 2 }}>
        {/* Dark Mode Toggle */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <ListItem disablePadding>
            <ListItemButton
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1,
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {darkMode ? <DarkModeIcon /> : <LightModeIcon />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="subtitle2">
                    {darkMode ? 'Dark Mode' : 'Light Mode'}
                  </Typography>
                }
              />
              <Switch
                checked={darkMode}
                onChange={onToggleDarkMode}
                size="small"
                sx={{
                  '& .MuiSwitch-thumb': {
                    background: darkMode ? theme.palette.primary.main : '#fbbf24',
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        </motion.div>

        {/* Notification Settings */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
        >
          <ListItem disablePadding>
            <ListItemButton
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1,
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <NotificationsIcon />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="subtitle2">
                    Notifications
                  </Typography>
                }
              />
              <Badge
                badgeContent={Object.values(notifications).reduce((a, b) => a + b, 0)}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.625rem',
                  },
                }}
              >
                <Box width={20} />
              </Badge>
            </ListItemButton>
          </ListItem>
        </motion.div>

        {/* App Version */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            KashLegal GBP Manager v1.0.0
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      disableSwipeToOpen={false}
      sx={{
        '& .MuiDrawer-paper': {
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: -320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -320, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <DrawerContent />
          </motion.div>
        )}
      </AnimatePresence>
    </SwipeableDrawer>
  );
};

export default MobileNav;